/**
 * Pedir entrar a un club, y —del otro lado— responder a quien pidió.
 *
 * Son dos caras de lo mismo y por eso viven juntas: lo que la persona elige
 * al pedir (su categoría) es exactamente lo que quien administra necesita ver
 * para decidir.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryKey, JOIN_POINTS_MAX, tablasQuePuedePedir } from '@tennisup/contracts';
import type {
  CategoryLevel,
  Community,
  JoinRequest,
  MyRanking,
  ReviewRankingRequest,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Paginador, usePaginado } from './Paginado';
import { PersonCell } from './PersonCell';
import { ChoiceGroup } from './ChoiceGroup';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { whatsappHref as enlaceDeWhatsapp } from '../lib/whatsapp';
import { categoryName, clubLevels, rankingName } from '../lib/categoryName';

/**
 * EL BLOQUE QUE VE QUIEN SÍ ESTÁ EN EL CLUB: cuál es su categoría.
 *
 * Vive al lado de `JoinBox` y en el mismo lugar de la pantalla porque son la
 * misma pregunta contestada de dos formas —«¿qué soy yo en este club?»—: uno
 * la contesta con un formulario para entrar, el otro con el nivel que ya se
 * tiene. Nunca aparecen los dos.
 *
 * POR QUÉ HACE FALTA, si el nivel ya estaba en la lista de jugadores: ahí hay
 * que buscarse a uno mismo entre doscientos. Y es el dato que decide en qué
 * torneos alguien se puede inscribir y contra quién compite en el ranking, o
 * sea lo primero que se viene a mirar.
 *
 * LA CATEGORÍA ES NIVEL × SEXO y se muestra completa: «B · Masculino». Solo
 * el nivel deja a medias la única parte que la gente confunde — hay un
 * ranking B masculino y otro B femenino, y no son el mismo.
 */
export function MyCategoryBox({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: (community: Community) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [pidiendo, setPidiendo] = useState<'nivel' | 'tabla' | null>(null);
  const [level, setLevel] = useState<CategoryLevel | null>(null);
  const [tabla, setTabla] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Quien creó el club puede administrarlo sin jugar en él: ahí no hay
  // categoría que mostrar, y no es un error.
  if (community.myLevel === null) return null;

  const sex = session?.user.sex ?? null;
  // Las del club para su sexo, menos la que ya tiene: pedir la propia no es
  // pedir nada, y el servidor lo rechaza.
  const disponibles = sex === null ? [] : clubLevels(community.categories, sex, community.myLevel);

  /*
   * LAS TABLAS QUE TODAVÍA PUEDE PEDIR, calculadas con la MISMA función que
   * usa el servidor para aceptar o rechazar (vive en `@tennisup/contracts`).
   *
   * Reimplementar la regla acá sería la forma de que la pantalla ofrezca
   * tablas que el servidor rechaza —o, peor, esconda las que sí aceptaría,
   * porque eso nadie lo reporta: simplemente no se usa—.
   */
  const puedePedir = tablasQuePuedePedir(community.myRankings, community.categories);

  async function pedir(): Promise<void> {
    if (level === null) return;
    setSending(true);
    setError(null);
    try {
      onChanged(await api.requestCategoryChange(community.id, level));
      setPidiendo(null);
      setLevel(null);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  async function pedirTabla(): Promise<void> {
    /*
     * LA TABLA ELEGIDA SE BUSCA EN LA LISTA, no se parte la clave.
     *
     * `tabla` guarda `categoryKey(...)` —un texto— porque es lo que puede
     * llevar un radio. Partirlo para recuperar nivel y sexo sería escribir
     * el revés de `categoryKey` acá, y el día que esa función cambie de
     * formato esto mandaría basura al servidor sin que nada avise. Buscar el
     * objeto original no puede desincronizarse.
     */
    const elegida = puedePedir.find((x) => categoryKey(x) === tabla) ?? null;
    if (elegida === null) return;
    setSending(true);
    setError(null);
    try {
      onChanged(
        await api.askForRanking(community.id, { level: elegida.level, sex: elegida.sex }),
      );
      setPidiendo(null);
      setTabla(null);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  async function cancelarTabla(ranking: MyRanking): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(
        await api.cancelRankingRequest(community.id, {
          level: ranking.level,
          sex: ranking.sex,
        }),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  async function cancelar(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(await api.cancelCategoryChange(community.id));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="my-category">
      <h2>{t('members.myCategory')}</h2>

      {/*
        UNA ETIQUETA POR TABLA, Y CADA UNA CON EL SEXO DE SU TABLA.

        Acá había UNA etiqueta que sacaba el nivel de `community.myLevel` y el
        sexo del PERFIL. Eso dio lo mismo mientras cada quien estuviera en una
        sola tabla —la de su sexo—, y dejó de darlo el día que alguien pudo
        pedir la otra: para una mujer inscrita en el ranking masculino, el
        perfil decía «C femenino», una tabla en la que no está.

        NIVEL Y SEXO, SIN LA DISCIPLINA, que es como estaba y como se lee
        mejor: hoy todos los rankings son de singles, así que «B · Singles
        masculino» agrega una palabra que no distingue nada. La lista de
        socios sí la muestra —usa `categoryName`— porque ahí conviven
        categorías de torneo, donde los dobles ya existen.
      */}
      <p className="community-tags">
        {community.myRankings.map((r) => (
          <span
            key={`${r.level}-${r.sex}-${r.discipline}`}
            className={`tag tag-category${r.status === 'PENDING' ? ' tag-pending' : ''}`}
          >
            {rankingName(t, r)}
            {r.status === 'PENDING' && ` · ${t('members.rankingWaiting')}`}
          </span>
        ))}
      </p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/*
        CANCELAR LO QUE PEDÍ, una línea por tabla esperando.

        Va aparte de las etiquetas de arriba y no adentro: una etiqueta con un
        botón adentro se toca sin querer, y acá tocarlo sin querer borra un
        pedido que puede llevar días esperando.
      */}
      {community.myRankings
        .filter((r) => r.status === 'PENDING')
        .map((r) => (
          <p className="links" key={`cancel-${r.level}-${r.sex}`}>
            <button
              type="button"
              className="link"
              disabled={sending}
              onClick={() => void cancelarTabla(r)}
            >
              {t('members.cancelRankingRequest', { category: rankingName(t, r) })}
            </button>
          </p>
        ))}

      {/*
        LO PEDIDO SE MUESTRA JUNTO A LO ACTUAL, no en vez de.

        Mostrar solo lo pedido haría creer que ya cambió; mostrar solo lo
        actual escondería que hay algo esperando, y la persona lo pediría otra
        vez. Con los dos a la vista se entiende que falta una respuesta.
      */}
      {community.myRequestedLevel !== null ? (
        <>
          <p className="help">
            {t('members.categoryRequested', {
              level: t(`categoryLevel.${community.myRequestedLevel}`),
            })}
          </p>
          <div className="button-row">
            <button type="button" className="link" disabled={sending} onClick={() => void cancelar()}>
              {t('members.cancelCategoryRequest')}
            </button>
          </div>
        </>
      ) : pidiendo === 'nivel' ? (
        <>
          <ChoiceGroup
            id={`category-request-${community.id}`}
            legend={t('members.askCategory')}
            help={t('members.askCategoryHelp')}
            value={level}
            options={disponibles.map((nivel) => ({
              value: nivel,
              label: t(`categoryLevel.${nivel}`),
            }))}
            onChange={setLevel}
            disabled={sending}
          />
          <div className="button-row">
            <button type="button" disabled={level === null || sending} onClick={() => void pedir()}>
              {sending ? t('common.sending') : t('members.sendCategoryRequest')}
            </button>
            <button
              type="button"
              className="link"
              disabled={sending}
              onClick={() => setPidiendo(null)}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : pidiendo === 'tabla' ? (
        <>
          {/*
            UNA SOLA LISTA, Y CADA BOTÓN DICE LA TABLA ENTERA.

            Acá había DOS pasos: primero el nivel, después masculino o
            femenino. La idea era que elegir el sexo fuera una decisión
            visible y no la mitad de una etiqueta larga. Salió al revés.

            Javier lo dijo mirándolo: «me confunde mucho, como Vero anotarme
            en C Masculino». Y tiene razón — eso es UNA decisión, no dos. Con
            dos pasos, el primero pedía elegir «C» sin saber cuál de los dos
            C, y la ambigüedad quedaba justo en el botón que se toca primero.

            La lista larga que quería evitar no es tal: solo salen las tablas
            que el club corre y en las que la persona no está, así que para
            Vero son once y no doce. Y once botones que dicen exactamente qué
            son se leen mejor que cuatro que no lo dicen.
          */}
          <ChoiceGroup
            id={`ranking-tabla-${community.id}`}
            legend={t('members.askRankingLevel')}
            help={t('members.askRankingHelp')}
            value={tabla}
            options={puedePedir.map((x) => ({
              value: categoryKey(x),
              label: rankingName(t, x),
            }))}
            onChange={setTabla}
            disabled={sending}
          />

          <div className="button-row">
            <button
              type="button"
              disabled={tabla === null || sending}
              onClick={() => void pedirTabla()}
            >
              {sending ? t('common.sending') : t('members.sendRankingRequest')}
            </button>
            <button
              type="button"
              className="link"
              disabled={sending}
              onClick={() => {
                setPidiendo(null);
                setTabla(null);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : (
        <p className="links">
          {/*
            Sin ninguna otra categoría posible no se ofrece pedir nada: sería
            un botón que abre una lista vacía.
          */}
          {disponibles.length > 0 && (
            <button
              type="button"
              className="link"
              onClick={() => {
                setPidiendo('nivel');
                setLevel(null);
              }}
            >
              {t('members.askCategory')}
            </button>
          )}
          {/*
            EL BOTÓN DE LA SEGUNDA TABLA DESAPARECE AL LLEGAR AL LÍMITE, y no
            se queda para dar un error al final: `tablasQuePuedePedir` devuelve
            vacío, y un botón que abre una lista vacía es peor que no tenerlo.
          */}
          {puedePedir.length > 0 && (
            <button
              type="button"
              className="link"
              onClick={() => {
                setPidiendo('tabla');
                setTabla(null);
              }}
            >
              {t('members.askRanking')}
            </button>
          )}
        </p>
      )}
    </section>
  );
}

/**
 * El bloque que ve quien NO está en el club.
 *
 * LA CATEGORÍA ES OBLIGATORIA para pedir, y solo se ofrecen las del sexo que
 * la persona tiene en su perfil: una categoría es nivel × sexo, y ofrecer las
 * demás sería ofrecer cuadros donde no puede jugar. Pedirla acá y no después
 * evita perseguir a alguien para que complete algo que ya podía elegir.
 */
export function JoinBox({
  community,
  onJoined,
  compact = false,
}: {
  community: Community;
  onJoined: (community: Community) => void;
  /**
   * Sin título ni explicación, para cuando ya está claro de qué club se
   * habla — en la lista, debajo de su tarjeta. En la ficha del club, en
   * cambio, es una sección de la pantalla y necesita su encabezado.
   */
  compact?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { session } = useAuth();
  /**
   * ARRANCA EN EL NIVEL QUE LA PERSONA DECLARÓ, si el club lo corre.
   *
   * Es una sugerencia, no una decisión: se puede cambiar antes de mandar, y
   * lo que quede fijado lo decide el club al aprobar. Sin esto, quien ya dijo
   * en su perfil que juega en B tiene que volver a decirlo en cada club.
   */
  const declarado = session?.user.declaredLevel ?? null;
  const [level, setLevel] = useState<CategoryLevel | null>(null);
  /**
   * LOS PUNTOS QUE PIDE TRAER DE OTRO RANKING.
   *
   * CERRADO DE ENTRADA. La mayoría entra sin nada que pedir, y un campo de
   * puntos a la vista en el formulario de ingreso invita a ponerse un número
   * «por si acaso» — y cada uno de esos números es una decisión más que
   * alguien tiene que tomar a mano.
   */
  const [conPuntos, setConPuntos] = useState(false);
  const [puntos, setPuntos] = useState('');
  const [nota, setNota] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Quien ya está adentro —o ya pidió— no ve un formulario para pedir.
  if (community.myMembershipStatus === 'ACTIVE') return null;

  if (community.myMembershipStatus === 'PENDING') {
    return <Alert kind="info">{t('join.pendingNotice')}</Alert>;
  }

  if (community.status !== 'APPROVED') return null;

  const sex = session?.user.sex ?? null;

  // Sin sexo en el perfil no hay ninguna categoría posible. Se dice con esas
  // palabras y con el enlace, en vez de mostrar una lista vacía.
  if (sex === null) {
    return <Alert kind="info">{t('join.needsProfile')}</Alert>;
  }

  const disponibles = clubLevels(community.categories, sex);
  if (disponibles.length === 0) {
    return <Alert kind="info">{t('join.noCategoryForSex')}</Alert>;
  }

  // El declarado solo se ofrece si el club CORRE esa categoría: sugerir una
  // que no existe ahí sería marcar una opción que no está en la lista.
  const sugerido = disponibles.some((nivel) => nivel === declarado) ? declarado : null;
  const elegido = level ?? sugerido;

  async function pedir(): Promise<void> {
    if (elegido === null) return;
    setSending(true);
    setError(null);
    try {
      const pedidos = Number.parseInt(puntos, 10);
      onJoined(
        await api.requestJoin(
          community.id,
          elegido,
          // Sin la casilla prendida, o con un número que no se entiende, no se
          // pide nada: mandar un cero haría que la solicitud espere aprobación
          // por unos puntos que nadie pidió.
          conPuntos && Number.isFinite(pedidos) && pedidos > 0
            ? { points: pedidos, note: nota }
            : undefined,
        ),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="join-box">
      {!compact && (
        <>
          <h2>{t('join.title')}</h2>
          <p className="help">{t('join.help')}</p>
        </>
      )}

      {error !== null && <Alert kind="error">{error}</Alert>}

      <ChoiceGroup
        // El id lleva el club adentro: en la lista hay un formulario por
        // tarjeta, y con un id fijo todas las etiquetas apuntarían al primero
        // —tocar «B» en el tercer club marcaría la del primero—.
        id={`join-level-${community.id}`}
        legend={t('join.chooseLevel')}
        help={t('join.chooseLevelHelp')}
        hint={t('common.required')}
        value={elegido}
        options={disponibles.map((nivel) => ({
          value: nivel,
          label: t(`categoryLevel.${nivel}`),
        }))}
        onChange={setLevel}
        disabled={sending}
      />

      {/* TRAER PUNTOS DE OTRO RANKING. Cerrado de entrada: la mayoría entra
          sin nada que pedir. */}
      <label className="check">
        <input
          type="checkbox"
          checked={conPuntos}
          disabled={sending}
          onChange={(e) => setConPuntos(e.target.checked)}
        />
        <span>{t('join.bringPoints')}</span>
      </label>

      {conPuntos && (
        <>
          {/* SE DICE QUE ESPERA APROBACIÓN, y antes de mandar. Quien pide
              puntos en un club que acepta a todo el mundo de una va a esperar
              entrar de inmediato, y enterarse después de que quedó esperando
              —sin saber por qué— es peor que no poder pedirlos. */}
          <p className="help">{t('join.bringPointsHelp')}</p>

          <div className="field">
            <label htmlFor={`join-points-${community.id}`}>{t('join.points')}</label>
            <input
              id={`join-points-${community.id}`}
              type="number"
              inputMode="numeric"
              min={1}
              /*
                EL TECHO SALE DEL CONTRATO Y NO DE UN 500 ESCRITO ACÁ.
                Escrito a mano, la pantalla y el servidor podían decir cosas
                distintas: el campo aceptaba lo que el servidor rechazaba, o
                al revés. Ahora es el mismo número en los dos lados, y no es
                un límite de política —es la red contra el desborde del
                entero de la base; ver `JOIN_POINTS_MAX`—.
              */
              max={JOIN_POINTS_MAX}
              value={puntos}
              disabled={sending}
              onChange={(e) => setPuntos(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor={`join-points-note-${community.id}`}>{t('join.pointsNote')}</label>
            <input
              id={`join-points-note-${community.id}`}
              type="text"
              autoComplete="off"
              maxLength={300}
              placeholder={t('join.pointsNotePlaceholder')}
              value={nota}
              disabled={sending}
              onChange={(e) => setNota(e.target.value)}
            />
          </div>
        </>
      )}

      <div className="button-row">
        <button type="button" disabled={elegido === null || sending} onClick={() => void pedir()}>
          {sending ? t('common.sending') : t('join.send')}
        </button>
      </div>
    </section>
  );
}

/**
 * UNA COLA DE DECISIONES, PARA QUIEN ADMINISTRA EL CLUB.
 *
 * Va ARRIBA de todo lo demás en la ficha: es lo único de la pantalla que le
 * pide una decisión a quien la mira, y enterrarla debajo de la lista de
 * jugadores es cómo alguien espera tres semanas por una respuesta.
 *
 * POR QUÉ SON TRES SECCIONES Y NO UNA CON TRES CASOS.
 *
 * Eran una sola. Bajo «Solicitudes de ingreso» salían mezcladas tres cosas
 * que se parecen en la pantalla —una persona, una categoría, dos botones— y
 * se contestan por rutas distintas:
 *
 *   1. alguien que quiere ENTRAR al club,
 *   2. un socio que pide una SEGUNDA CATEGORÍA,
 *   3. un socio que pide que le reconozcan PUNTOS de otro ranking.
 *
 * Los botones eran los de (1) para las tres. Aprobar (2) o (3) llamaba a la
 * ruta de ingreso sobre alguien que ya era socio: 404. Y rechazar llamaba a
 * `DELETE /join-requests/:userId`, que BORRA LA MEMBRESÍA — o sea que el
 * botón de «no te doy la segunda categoría» sacaba a la persona del club.
 *
 * Lo encontró Javier en producción: «tambien si rechazo que se anote a otra
 * categoria lo saca del club».
 *
 * La lección, escrita acá porque es donde se iba a repetir: lo que separa
 * estas listas no es qué muestran —muestran lo mismo— sino QUÉ BOTÓN LAS
 * CONTESTA. Una lista mezclada obliga a la fila a adivinar cuál le toca, y
 * un día adivina mal. Tres colas, tres juegos de botones, y `acciones` se lo
 * da cada una: ninguna fila puede llamar a una ruta que no es la suya.
 */
function ColaDeDecisiones({
  community,
  titulo,
  ayuda,
  cargar,
  acciones,
  onChanged,
  className,
}: {
  community: Community;
  titulo: string;
  ayuda: string;
  cargar: (id: string) => Promise<JoinRequest[]>;
  /** Los botones de ESTA cola, y ningunos otros. */
  acciones: (
    request: JoinRequest,
    responder: (userId: string, accion: () => Promise<unknown>) => void,
    ocupado: boolean,
  ) => JSX.Element;
  onChanged: () => void;
  className: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<JoinRequest[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const traído = await cargar(community.id);
      // SE COMPRUEBA QUE SEA UNA LISTA, y no se confía en el tipo. `JoinRequest[]`
      // es una promesa del compilador sobre lo que el servidor DEBERÍA mandar, y
      // el compilador no ve la respuesta: una versión vieja del servidor, un proxy
      // que devuelve un error en JSON o una ruta que todavía no existe entregan un
      // objeto, `filas.slice` explota y la PANTALLA ENTERA se cae en blanco —no
      // solo esta sección—. Tres colas multiplican por tres las respuestas en las
      // que hay que confiar, así que no se confía en ninguna.
      setRequests(Array.isArray(traído) ? traído : []);
    } catch {
      // Sin permiso o sin red: no se muestra nada. Un error rojo por una cola
      // que quizá esté vacía es peor que no mostrarla.
      setRequests([]);
    }
  }, [community.id, cargar]);

  useEffect(() => {
    void load();
  }, [load]);

  function responder(userId: string, accion: () => Promise<unknown>): void {
    setBusyId(userId);
    setError(null);
    void (async () => {
      try {
        await accion();
        await load();
        // Se recarga la ficha entera: aprobar cambia el total de jugadores y la
        // lista, y dejar esos dos números viejos es cómo alguien aprueba dos
        // veces a la misma persona.
        onChanged();
      } catch (caught) {
        setError(errorMessage(t, caught));
      } finally {
        setBusyId(null);
      }
    })();
  }

  // DE A DIEZ: un club que abre inscripciones junta cuarenta solicitudes en
  // una tarde, y cada una trae foto, teléfono y dos botones.
  const pagina = usePaginado(requests ?? []);

  if (requests === null || requests.length === 0) return null;

  return (
    <section className={className}>
      <div className="section-head">
        <h2>{titulo}</h2>
        <span className="tag tag-pending">{t('join.waiting', { count: requests.length })}</span>
      </div>
      <p className="help">{ayuda}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="table-scroll">
      <table className="people-table">
        <thead>
          <tr>
            <th scope="col">{t('player.player')}</th>
            <th scope="col">{t('player.contact')}</th>
            <th scope="col">{t('ranking.category')}</th>
            <th scope="col">
              <span className="visually-hidden">{t('player.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
        {pagina.visibles.map((request) => (
          <tr key={request.userId}>
            <PersonCell
              userId={request.userId}
              displayName={request.displayName}
              avatarPath={request.avatarPath}
              note={
                request.locationLabel === ''
                  ? request.fullName
                  : `${request.fullName} · ${request.locationLabel}`
              }
            />

            {/* LOS DATOS PARA DECIDIR, en su propia columna. Con el apodo y
                el nombre legal solos, aprobar es decirle que sí a un
                desconocido: la edad ubica a la persona —un club sabe si
                espera a alguien de 15 o de 50— y el teléfono es cómo se
                confirma acá: se le escribe.

                EL TELÉFONO NO VA ADENTRO DE LA CELDA DEL NOMBRE: esa celda
                entera es el enlace al perfil, y un enlace no puede contener
                otro. */}
            <td>
              <span className="help">
                {t('join.age', { count: request.age })}
                {/* El enlace lo arma `whatsappHref`, el mismo del cuadro: el
                    número va sin «+» ni espacios, y esa es la parte que se
                    equivoca sola. Escrito dos veces, se corrige una sola. */}
                {enlaceDeWhatsapp(request.whatsappPhone) !== null && (
                  <>
                    {' · '}
                    <a
                      href={enlaceDeWhatsapp(request.whatsappPhone) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {request.whatsappPhone}
                    </a>
                  </>
                )}
              </span>
            </td>
            <td>
            <span className="tag tag-category">
              {/* AHORA SÍ ES `categoryName`, y el cambio no es cosmético.
                  Una solicitud de ingreso ya no trae el sexo DE LA PERSONA:
                  trae LA TABLA en la que pide competir, con su disciplina.
                  Escribir «Masculino» al lado de una mujer que pide entrar
                  al ranking masculino diría algo falso sobre ella; «Singles
                  masculino» dice lo que de verdad está pidiendo. */}
              {categoryName(t, request)}
            </span>
            {/* LOS PUNTOS QUE PIDIÓ TRAER, con su explicación.
                VAN JUNTO A LA CATEGORÍA y no escondidos: es lo único de esta
                fila que decide algo más que «entra o no entra», y aprobar sin
                verlo sería regalar puntos sin saberlo. */}
            {request.requestedPoints !== null && request.requestedPoints > 0 && (
              <span className="help join-points">
                {t('join.asksFor', { count: request.requestedPoints })}
                {request.requestedPointsNote !== null && ` · ${request.requestedPointsNote}`}
              </span>
            )}
            </td>
            <td className="actions">
            <span className="join-actions">
              {acciones(request, responder, busyId === request.userId)}
            </span>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
      </div>
      <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
    </section>
  );
}

/**
 * QUIEN QUIERE ENTRAR AL CLUB. La cola original, y la única de las tres
 * donde rechazar borra algo: acá la persona todavía no es socia, así que
 * borrar su solicitud la deja exactamente como estaba antes de pedir.
 */
export function JoinRequests({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  return (
    <ColaDeDecisiones
      community={community}
      className="join-requests"
      titulo={t('join.requestsTitle')}
      ayuda={t('join.requestsHelp')}
      cargar={api.joinRequests}
      onChanged={onChanged}
      acciones={(request, responder, ocupado) => (
        <>
          <button
            type="button"
            disabled={ocupado}
            onClick={() =>
              responder(request.userId, () =>
                // SIN DECIR NADA SE CONCEDE LO QUE PIDIÓ: quien administra
                // ya dijo que sí a esta solicitud, y la solicitud incluía
                // los puntos. Para dar menos está el otro botón.
                api.approveJoin(community.id, request.userId),
              )
            }
          >
            {t('join.approve')}
          </button>

          {/* APROBAR SIN LOS PUNTOS. Es la mitad del «aprueba o rechaza»:
              sin esto, la única respuesta a «pido 500» es sí o no, y el
              club termina diciéndole que no a alguien que sí quería
              adentro. Solo sale cuando hay puntos que negar. */}
          {request.requestedPoints !== null && request.requestedPoints > 0 && (
            <button
              type="button"
              className="secondary"
              disabled={ocupado}
              onClick={() =>
                responder(request.userId, () => api.approveJoin(community.id, request.userId, 0))
              }
            >
              {t('join.approveWithoutPoints')}
            </button>
          )}
          <button
            type="button"
            className="link danger"
            disabled={ocupado}
            onClick={() => responder(request.userId, () => api.rejectJoin(community.id, request.userId))}
          >
            {t('join.reject')}
          </button>
        </>
      )}
    />
  );
}

/**
 * LOS PEDIDOS DE SEGUNDA CATEGORÍA, de gente que YA ESTÁ ADENTRO.
 *
 * RECHAZAR ACÁ NO SACA DEL CLUB, y por eso la sección existe. Se contesta
 * con `reviewRankingRequest`, que solo toca ESA tabla: el «no» le borra el
 * pedido y le deja intacta la categoría en la que ya compite.
 */
export function RankingRequests({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  /**
   * LA TABLA QUE PIDIÓ, que es lo que se aprueba o se rechaza, y no «esa
   * persona»: alguien puede tener una tabla aprobada y otra esperando, y una
   * respuesta que no diga cuál terminaría contestando la equivocada.
   */
  function tablaDe(request: JoinRequest, approve: boolean): ReviewRankingRequest {
    return { level: request.level, sex: request.sex, approve };
  }

  return (
    <ColaDeDecisiones
      community={community}
      className="join-requests"
      titulo={t('join.rankingRequestsTitle')}
      ayuda={t('join.rankingRequestsHelp')}
      cargar={api.rankingRequests}
      onChanged={onChanged}
      acciones={(request, responder, ocupado) => (
        <>
          <button
            type="button"
            disabled={ocupado}
            onClick={() =>
              responder(request.userId, () =>
                api.reviewRankingRequest(community.id, request.userId, tablaDe(request, true)),
              )
            }
          >
            {t('join.approve')}
          </button>
          <button
            type="button"
            className="link danger"
            disabled={ocupado}
            onClick={() =>
              responder(request.userId, () =>
                api.reviewRankingRequest(community.id, request.userId, tablaDe(request, false)),
              )
            }
          >
            {t('join.reject')}
          </button>
        </>
      )}
    />
  );
}

/**
 * LOS PEDIDOS DE PUNTOS de gente que YA ESTÁ ADENTRO.
 *
 * NO HAY BOTÓN DE RECHAZAR con un `DELETE`, a propósito: decir que no es
 * conceder cero, y eso no toca la membresía. El `DELETE` de esta pantalla
 * es el que borraba socios.
 */
export function PointRequests({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  return (
    <ColaDeDecisiones
      community={community}
      className="join-requests"
      titulo={t('join.pointRequestsTitle')}
      ayuda={t('join.pointRequestsHelp')}
      cargar={api.pointRequests}
      onChanged={onChanged}
      acciones={(request, responder, ocupado) => (
        <>
          <button
            type="button"
            disabled={ocupado}
            onClick={() =>
              responder(request.userId, () =>
                api.reviewPointRequest(community.id, request.userId),
              )
            }
          >
            {t('join.givePoints')}
          </button>
          <button
            type="button"
            className="secondary"
            disabled={ocupado}
            onClick={() =>
              responder(request.userId, () =>
                api.reviewPointRequest(community.id, request.userId, 0),
              )
            }
          >
            {t('join.denyPoints')}
          </button>
        </>
      )}
    />
  );
}

/**
 * El interruptor de auto-aceptar.
 *
 * VIVE APARTE DE LA COLA DE SOLICITUDES, y eso es a propósito: la cola
 * desaparece cuando está vacía —una sección vacía es ruido— y justo con la
 * cola vacía es cuando alguien quiere prender el auto-aceptar. Adentro,
 * el interruptor se iría con ella.
 *
 * Dice EN PALABRAS lo que va a pasar, en presente y no en condicional: «las
 * solicitudes nuevas entran solas» describe el estado en el que quedó el
 * club. Una etiqueta como «auto-aprobación» obliga a probarlo para saber qué
 * hace.
 */
export function AutoApproveToggle({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: (community: Community) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function cambiar(value: boolean, conPuntos: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.setAutoApprove(community.id, value, conPuntos));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auto-approve">
      <label className="check">
        <input
          type="checkbox"
          checked={community.autoApproveMembers}
          disabled={busy}
          // Apagar el de arriba apaga el de adentro: «no acepto a nadie solo,
          // pero sí a quien pide puntos» no es un estado que alguien quiera.
          // El servidor lo normaliza igual; acá se manda ya coherente para
          // que la pantalla no muestre un instante lo que no va a quedar.
          onChange={(e) =>
            void cambiar(e.target.checked, e.target.checked && community.autoApproveWithPoints)
          }
        />
        <span>{t('join.autoApprove')}</span>
      </label>
      <p className="help">
        {community.autoApproveMembers ? t('join.autoApproveOn') : t('join.autoApproveOff')}
      </p>

      {/*
        EL DE LOS PUNTOS VA ADENTRO DEL OTRO, y solo aparece cuando el de
        arriba está prendido. No es un adorno: sin auto-aceptar, este
        interruptor no significa nada, y mostrarlo apagado al lado haría creer
        que son dos decisiones sueltas. Lo guardado no se pierde al apagar el
        de arriba —vuelve como estaba si se vuelve a prender—.
      */}
      {community.autoApproveMembers && (
        <div className="auto-approve-points">
          <label className="check">
            <input
              type="checkbox"
              checked={community.autoApproveWithPoints}
              disabled={busy}
              onChange={(e) => void cambiar(true, e.target.checked)}
            />
            <span>{t('join.autoApproveWithPoints')}</span>
          </label>
          <p className="help">
            {community.autoApproveWithPoints
              ? t('join.autoApproveWithPointsOn')
              : t('join.autoApproveWithPointsOff')}
          </p>
        </div>
      )}
      {/* Se aclara acá y no en un cartel aparte: prender el interruptor NO
          aprueba a los que ya están esperando, y quien lo prende para vaciar
          la cola tiene que enterarse en el momento, no después. */}
      {community.pendingRequests > 0 && community.autoApproveMembers && (
        <p className="help">{t('join.autoApproveNotRetroactive')}</p>
      )}
      {error !== null && <Alert kind="error">{error}</Alert>}
    </div>
  );
}

/**
 * SALIRSE DEL CLUB.
 *
 * Javier: «como jugador si no soy administrador quiero poder salirme del
 * club». Es el simétrico exacto de `JoinBox` y por eso vive al lado: uno
 * dibuja la puerta de entrada y el otro la de salida, y el que no corresponde
 * se borra solo.
 *
 * NO SE DIBUJA PARA QUIEN ADMINISTRA, y eso no es esconder un error: el
 * servidor también lo rechaza, con `ADMIN_CANNOT_LEAVE`. Pero un botón que
 * siempre contesta que no es peor que ningún botón — quien administra tiene
 * que soltar el permiso primero, y eso se hace en otra parte de esta misma
 * pantalla.
 *
 * PREGUNTA ANTES, y dice qué se pierde. Salirse no es reversible de un toque:
 * volver es pedir entrar otra vez y esperar a que lo aprueben. Lo que se
 * pierde —los puntos dejan de contar en ese ranking— va en la pregunta,
 * porque es lo que la gente no se imagina.
 */
export function LeaveBox({
  community,
  onLeft,
}: {
  community: Community;
  /** Se llama cuando ya salió: quien llama recarga la pantalla. */
  onLeft: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [preguntando, setPreguntando] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Quien no está adentro no tiene de dónde salirse; quien administra tampoco.
  if (community.myMembershipStatus !== 'ACTIVE') return null;
  if (community.myRole === 'ADMIN' || community.isMine) return null;

  async function salir(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      await api.leaveCommunity(community.id);
      onLeft();
    } catch (caught) {
      setError(errorMessage(t, caught));
      setSending(false);
    }
  }

  if (!preguntando) {
    return (
      <p className="leave-box">
        <button type="button" className="link danger" onClick={() => setPreguntando(true)}>
          {t('join.leave')}
        </button>
      </p>
    );
  }

  return (
    <div className="leave-box">
      {error !== null && <Alert kind="error">{error}</Alert>}
      <p className="help">{t('join.leaveAsk', { name: community.name })}</p>
      <div className="button-row">
        <button
          type="button"
          className="link danger"
          disabled={sending}
          onClick={() => void salir()}
        >
          {sending ? t('common.sending') : t('join.leaveYes')}
        </button>
        <button
          type="button"
          className="link"
          disabled={sending}
          onClick={() => setPreguntando(false)}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
